import React, { useState, useEffect, useRef } from 'react';

interface TerminalProps {
  onNavigate?: (path: string) => void;
  className?: string;
}

interface TerminalHistory {
  type: 'command' | 'output' | 'error' | 'success' | 'warning';
  content: string;
  timestamp?: string;
}

const Terminal: React.FC<TerminalProps> = ({ onNavigate, className = '' }) => {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState<TerminalHistory[]>([]);
  const [isMinimized, setIsMinimized] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const historyRef = useRef<HTMLDivElement>(null);

  // Available commands and their descriptions
  const commands = {
    help: {
      description: 'Show available commands',
      action: () => showHelp()
    },
    clear: {
      description: 'Clear terminal history',
      action: () => clearTerminal()
    },
    store: {
      description: 'Navigate to digital store',
      action: () => navigateTo('/store', 'Accessing digital arsenal...', 'success')
    },
    about: {
      description: 'Load neural profile',
      action: () => navigateTo('/about', 'Loading neural profile...', 'success')
    },
    projects: {
      description: 'Browse project archives',
      action: () => navigateTo('/projects', 'Accessing project archives...', 'success')
    },
    articles: {
      description: 'Browse article database',
      action: () => navigateTo('/articles', 'Accessing article database...', 'success')
    },
    contact: {
      description: 'Establish communication link',
      action: () => navigateTo('/contact', 'Establishing comm link...', 'success')
    },
    home: {
      description: 'Return to main interface',
      action: () => navigateTo('/', 'Returning to main interface...', 'success')
    },
    status: {
      description: 'Show system status',
      action: () => showStatus()
    },
    whoami: {
      description: 'Display current user info',
      action: () => showWhoami()
    },
    matrix: {
      description: 'Enter the Matrix',
      action: () => showMatrix()
    },
    hack: {
      description: 'Initiate hacking sequence',
      action: () => showHack()
    }
  };

  const addToHistory = (entry: TerminalHistory) => {
    setHistory(prev => [...prev, {
      ...entry,
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  const navigateTo = (path: string, message: string, type: TerminalHistory['type'] = 'output') => {
    addToHistory({ type, content: message });
    
    // Add enhanced dramatic transition warning sequence
    addToHistory({ 
      type: 'warning', 
      content: '⚠️  REALITY BREACH DETECTED - PREPARING DIMENSIONAL JUMP...' 
    });
    
    addToHistory({ 
      type: 'error', 
      content: '🚨 INITIATING EMERGENCY PORTAL SEQUENCE...' 
    });
    
    addToHistory({ 
      type: 'success', 
      content: '🌀 BRACE FOR IMPACT! EPIC TRANSITION IN 3... 2... 1...' 
    });

    addToHistory({ 
      type: 'error', 
      content: '💥 REALITY IS ABOUT TO EXPLODE! HOLD ON TIGHT!' 
    });

    if (onNavigate) {
      // Longer delay to show the enhanced dramatic messages before triggering explosion
      setTimeout(() => {
        onNavigate(path);
      }, 1200); // Extended from 800ms to 1200ms for more dramatic effect
    }
  };

  const showHelp = () => {
    addToHistory({ type: 'success', content: '\n=== AVAILABLE COMMANDS ===' });
    Object.entries(commands).forEach(([cmd, info]) => {
      addToHistory({ 
        type: 'output', 
        content: `  ${cmd.padEnd(12)} - ${info.description}` 
      });
    });
    addToHistory({ type: 'output', content: '\n=== END COMMAND LIST ===' });
  };

  const showStatus = () => {
    addToHistory({ type: 'success', content: '\n=== SYSTEM STATUS ===' });
    addToHistory({ type: 'success', content: '  Connection: ONLINE' });
    addToHistory({ type: 'success', content: '  Security: ENCRYPTED' });
    addToHistory({ type: 'success', content: '  Neural Link: ACTIVE' });
    addToHistory({ type: 'success', content: '  Matrix Grid: SYNCHRONIZED' });
    addToHistory({ type: 'warning', content: '  Firewall: ENABLED' });
    addToHistory({ type: 'output', content: '=== STATUS CHECK COMPLETE ===' });
  };

  const showWhoami = () => {
    addToHistory({ type: 'success', content: '\n=== USER PROFILE ===' });
    addToHistory({ type: 'output', content: 'User: griffin@matrix' });
    addToHistory({ type: 'output', content: 'Role: Digital Architect' });
    addToHistory({ type: 'success', content: 'Access Level: ADMIN' });
    addToHistory({ type: 'output', content: 'Location: The Matrix' });
    addToHistory({ type: 'warning', content: 'Clearance: TOP SECRET' });
    addToHistory({ type: 'output', content: '=== PROFILE END ===' });
  };

  const showMatrix = () => {
    addToHistory({ type: 'success', content: '\n>>> Entering the Matrix...' });
    addToHistory({ type: 'warning', content: '>>> Red pill or blue pill?' });
    addToHistory({ type: 'success', content: '>>> Welcome to the real world.' });
  };

  const showHack = () => {
    addToHistory({ type: 'warning', content: '\n>>> INITIATING HACK SEQUENCE...' });
    addToHistory({ type: 'output', content: '>>> Scanning for vulnerabilities...' });
    addToHistory({ type: 'success', content: '>>> Access granted. You\'re in the mainframe.' });
    addToHistory({ type: 'warning', content: '>>> Remember: With great power comes great responsibility.' });
  };

  const clearTerminal = () => {
    setHistory([]);
    addToHistory({ type: 'output', content: 'Terminal cleared. Ready for input.' });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    // Add command to history
    addToHistory({ type: 'command', content: input });

    // Parse and execute command
    const [cmd, ...args] = input.toLowerCase().trim().split(' ');
    
    if (commands[cmd as keyof typeof commands]) {
      commands[cmd as keyof typeof commands].action();
    } else if (cmd === '') {
      // Do nothing for empty commands
    } else {
      addToHistory({ 
        type: 'error', 
        content: `Command not found: ${cmd}. Type "help" for available commands.` 
      });
    }

    setInput('');
  };

  const handleTerminalClick = () => {
    if (inputRef.current && !isMinimized) {
      inputRef.current.focus();
    }
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  useEffect(() => {
    // Initialize with welcome message
    setHistory([
      {
        type: 'success',
        content: '>>> GRIFFIN\'S DIGITAL REALM - Terminal Interface v3.0'
      },
      {
        type: 'output',
        content: '>>> Neural link established...'
      },
      {
        type: 'success',
        content: '>>> Matrix connection: ACTIVE'
      },
      {
        type: 'output',
        content: 'Type "help" for available commands.'
      }
    ]);
  }, []);

  useEffect(() => {
    // Auto-scroll to bottom when history updates
    if (historyRef.current) {
      historyRef.current.scrollTop = historyRef.current.scrollHeight;
    }
  }, [history]);

  useEffect(() => {
    // Auto-focus input when terminal is opened
    if (!isMinimized && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isMinimized]);

  return (
    <div className={`matrix-terminal ${className} ${isMinimized ? 'matrix-terminal--minimized' : ''}`}>
      <div className="matrix-terminal__header" onClick={toggleMinimize}>
        <div className="matrix-terminal__controls">
          <span className="matrix-terminal__dot matrix-terminal__dot--red"></span>
          <span className="matrix-terminal__dot matrix-terminal__dot--yellow"></span>
          <span className="matrix-terminal__dot matrix-terminal__dot--green"></span>
        </div>
        <div className="matrix-terminal__title">GRIFFIN@MATRIX:~$ NAVIGATION_TERMINAL_v3.0</div>
        <div className="matrix-terminal__status">
          <span className="matrix-terminal__indicator"></span>
          ONLINE
        </div>
      </div>
      
      {!isMinimized && (
        <div className="matrix-terminal__body" onClick={handleTerminalClick}>
          <div className="matrix-terminal__history" ref={historyRef}>
            {history.map((entry, index) => (
              <div 
                key={index} 
                className={`matrix-terminal__line matrix-terminal__line--${entry.type}`}
              >
                {entry.type === 'command' ? (
                  <>
                    <span className="matrix-terminal__prompt">griffin@matrix:~$</span>
                    <span className="matrix-terminal__command">{entry.content}</span>
                  </>
                ) : (
                  <span className={`matrix-terminal__output matrix-terminal__output--${entry.type}`}>
                    {entry.content}
                  </span>
                )}
              </div>
            ))}
          </div>
          
          <form onSubmit={handleSubmit} className="matrix-terminal__input-form">
            <div className="matrix-terminal__line matrix-terminal__line--active">
              <span className="matrix-terminal__prompt">griffin@matrix:~$</span>
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="matrix-terminal__input"
                placeholder="Enter command..."
                autoComplete="off"
                spellCheck="false"
              />
              <span className="matrix-terminal__cursor">█</span>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Terminal; 